import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';

const CurrencySwitcher = ({ className = '' }) => {
  const { currency, changeCurrency, currencies, currentCurrency } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    changeCurrency(code);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm text-gray-600 hover:text-black transition-colors px-2 py-1 rounded-md hover:bg-gray-50"
        data-testid="currency-switcher-btn"
        aria-label="Select currency"
        aria-expanded={isOpen}
      >
        <span className="font-medium">{currentCurrency?.code}</span>
        <span>{currentCurrency?.symbol}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] z-50"
          role="listbox"
        >
          {currencies.map((curr) => (
            <button
              key={curr.code}
              onClick={() => handleSelect(curr.code)}
              className={`w-full px-4 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${
                currency === curr.code ? 'bg-gray-50 font-medium' : ''
              }`}
              role="option"
              aria-selected={currency === curr.code}
              data-testid={`currency-option-${curr.code.toLowerCase()}`}
            >
              <span className="flex items-center gap-2">
                <span className="w-6 text-center font-mono">{curr.symbol}</span>
                <span>{curr.name}</span>
              </span>
              {currency === curr.code && (
                <Check className="w-4 h-4 text-green-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrencySwitcher;
